/**
 * Dashboard shell navigation — top-level items + grouped sections.
 *
 * Per Rule #14 (no inline literals) + Kiran 2026-05-15 (SOP 15 Ch.15):
 * placeholder nav entries for un-shipped phases were removed alongside
 * their D1 tables. When a phase ships, its nav entry is added next to
 * the new page component.
 *
 * Phase 2.4 (PermissionGate) will filter entries by the operator's
 * group bindings; for now every signed-in dashboard user sees
 * everything live.
 */

import { ROUTES } from './routes.constants'
import { SCREENCODES } from './screen.constants'

export interface DashboardNavItem {
  readonly to: string
  readonly label: string
  readonly end?: boolean
  /** screens.code — entry is shown only if the user has `view` on it. */
  readonly screen?: string
}

export interface DashboardNavGroup {
  readonly id: string
  readonly label: string
  readonly items: ReadonlyArray<DashboardNavItem>
}

/** Standalone entries rendered ABOVE the grouped sections. */
export const DASHBOARD_NAV_TOP: ReadonlyArray<DashboardNavItem> = [
  { to: ROUTES.DASHBOARD, label: 'Dashboard', end: true },
  { to: ROUTES.DASHBOARDTRAFFIC, label: 'Traffic', screen: SCREENCODES.DASHBOARDTRAFFIC }
]

export const DASHBOARD_NAV_GROUPS: ReadonlyArray<DashboardNavGroup> = [
  {
    id: 'iam',
    label: 'IAM',
    items: [
      { to: ROUTES.IAMUSERTYPES, label: 'User Types', screen: SCREENCODES.IAMUSERTYPES },
      { to: ROUTES.IAMSCREENACTIONS, label: 'Screen Actions', screen: SCREENCODES.IAMSCREENACTIONS },
      { to: ROUTES.IAMGROUPS, label: 'Groups', screen: SCREENCODES.IAMGROUPS },
      { to: ROUTES.IAMSTAFF, label: 'Staff', screen: SCREENCODES.IAMSTAFF }
    ]
  },
  {
    id: 'manage',
    label: 'Manage',
    items: [
      { to: ROUTES.MANAGEATTRIBUTES, label: 'Attributes', screen: SCREENCODES.MANAGEATTRIBUTES },
      { to: ROUTES.MANAGECUSTOMDATATYPES, label: 'Custom Data Types', screen: SCREENCODES.MANAGECUSTOMDATATYPES },
      { to: ROUTES.MANAGECATEGORIES, label: 'Categories', screen: SCREENCODES.MANAGECATEGORIES },
      { to: ROUTES.MANAGESUPPLIERS, label: 'Suppliers', screen: SCREENCODES.MANAGESUPPLIERS },
      { to: ROUTES.MANAGEPURCHASES, label: 'Purchases', screen: SCREENCODES.MANAGEPURCHASES },
      { to: ROUTES.MANAGEGSTRATES, label: 'GST Rates', screen: SCREENCODES.MANAGEGSTRATES },
      { to: ROUTES.MANAGEUNITS, label: 'Units', screen: SCREENCODES.MANAGEUNITS },
      { to: ROUTES.MANAGESTORES, label: 'Stores', screen: SCREENCODES.MANAGESTORES },
      { to: ROUTES.MANAGEORGANISATIONS, label: 'Organisations', screen: SCREENCODES.MANAGEORGANISATIONS },
      { to: ROUTES.MANAGEPRODUCTSUPPLIERS, label: 'Product Suppliers', screen: SCREENCODES.MANAGEPRODUCTSUPPLIERS },
      { to: ROUTES.MANAGEMATERIALSUPPLIERS, label: 'Material Suppliers', screen: SCREENCODES.MANAGEMATERIALSUPPLIERS }
    ]
  },
  {
    id: 'product',
    label: 'Product',
    items: [
      { to: ROUTES.PRODUCTS, label: 'Products', screen: SCREENCODES.PRODUCTS },
      { to: ROUTES.PRODUCTCOMPONENTS, label: 'Product Components', screen: SCREENCODES.PRODUCTCOMPONENTS },
      { to: ROUTES.SOLUTIONS, label: 'Solutions', screen: SCREENCODES.PRODUCTSOLUTIONS },
      { to: ROUTES.PRODUCTSOURCE, label: 'Product Source', screen: SCREENCODES.PRODUCTSOURCE },
      { to: ROUTES.PRODUCTPRICING, label: 'Product Pricing', screen: SCREENCODES.PRODUCTPRICING },
      { to: ROUTES.PRODUCTVARIANTPRICING, label: 'Variant Pricing', screen: SCREENCODES.PRODUCTVARIANTPRICING },
      { to: ROUTES.PRODUCTVARIANTPRICINGHISTORY, label: 'Variant Pricing History', screen: SCREENCODES.PRODUCTVARIANTPRICINGHISTORY }
    ]
  },
  // Manufacturing — raw-material catalogues + per-supplier pricing +
  // Material Purchases (allocation ledger, mirror of Warehouse > Product
  // Purchases, 2026-06-04) + Raw Material Stock History. The old
  // Manufacturing > Purchases screen was retired the same day; the
  // canonical supplier-invoice ledger lives on Manage > Purchases.
  {
    id: 'manufacturing',
    label: 'Manufacturing',
    items: [
      { to: ROUTES.MANUFACTURINGRAWMATERIALS, label: 'Raw Materials', screen: SCREENCODES.MANUFACTURINGRAWMATERIALS },
      { to: ROUTES.MANUFACTURINGPRICING, label: 'Manufacturing Pricing', screen: SCREENCODES.MANUFACTURINGPRICING },
      { to: ROUTES.MANUFACTURINGMATERIALPURCHASES, label: 'Material Purchases', screen: SCREENCODES.MANUFACTURINGMATERIALPURCHASES },
      { to: ROUTES.MANUFACTURINGRAWMATERIALSTOCKHISTORY, label: 'Raw Material Stock History', screen: SCREENCODES.MANUFACTURINGRAWMATERIALSTOCKHISTORY }
    ]
  },
  // Warehouse — per-variant stock movement ledger + Product Purchases
  // ledger (with the per-row Receive flow that appends stock movements
  // capped by invoice qty, 2026-06-03). The standalone Stock Entry
  // screen was retired alongside the Receive flow — every stock-in now
  // lands via Receive on a Product Purchase row.
  {
    id: 'warehouse',
    label: 'Warehouse',
    items: [
      { to: ROUTES.WAREHOUSEPRODUCTPURCHASES, label: 'Product Purchases', screen: SCREENCODES.WAREHOUSEPRODUCTPURCHASES },
      { to: ROUTES.WAREHOUSEPRODUCTSTOCKHISTORY, label: 'Product Stock History', screen: SCREENCODES.WAREHOUSEPRODUCTSTOCKHISTORY },
      { to: ROUTES.WAREHOUSEVARIANTSTOCKHISTORY, label: 'Variant Stock History', screen: SCREENCODES.WAREHOUSEVARIANTSTOCKHISTORY }
    ]
  },
  // Order Management — operator views of customer orders + returns
  // (2026-06-05). Read-only in v1; mutation actions land later.
  {
    id: 'order-management',
    label: 'Order Management',
    items: [
      { to: ROUTES.ORDERMANAGEMENTORDERS,        label: 'Orders',         screen: SCREENCODES.ORDERMANAGEMENTORDERS },
      { to: ROUTES.ORDERMANAGEMENTRETURNS,       label: 'Returns',        screen: SCREENCODES.ORDERMANAGEMENTRETURNS },
      { to: ROUTES.ORDERMANAGEMENTBUILDREQUESTS, label: 'Build Requests', screen: SCREENCODES.ORDERMANAGEMENTBUILDREQUESTS }
    ]
  },
  // Customers — read-only operator visibility into registered shoppers.
  // Data lives in iam_users (user_type_code='customer'); served by the
  // IAM worker at /iam/customers. Added 2026-05-17.
  {
    id: 'customers',
    label: 'Customers',
    items: [
      { to: ROUTES.CUSTOMERSREGISTERED, label: 'Registered Customers', screen: SCREENCODES.CUSTOMERSREGISTERED }
    ]
  },
  // Preferences holds admin-only/cross-cutting screens (Settings is
  // gated server-side on user_type='admin'; Email Templates is gated
  // by the IAM permission catalogue). Renamed 2026-05-19 from
  // "System" — the API surface lives in its own
  // preferences worker so the domain is decoupled from
  // identity. More ops/infra screens (worker health, gateway routing
  // browser, queue stats, module-flag toggles) land here next.
  {
    id: 'preferences',
    label: 'Preferences',
    items: [
      { to: ROUTES.PREFERENCESSETTINGS, label: 'Settings', screen: SCREENCODES.PREFERENCESSETTINGS },
      { to: ROUTES.PREFERENCESEMAILTEMPLATES, label: 'Email Templates', screen: SCREENCODES.PREFERENCESEMAILTEMPLATES },
      { to: ROUTES.PREFERENCESWEBHOOKPROVIDERS, label: 'Webhook Providers', screen: SCREENCODES.PREFERENCESWEBHOOKPROVIDERS }
    ]
  }
]
